import {
  Children,
  Fragment,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import s from './Dropdown.module.css';

/*
 * Branded dropdowns in place of the browser's own menus, which can't be styled: Select (a pick-one list, same props as
 * a native <select> with <option> children) and Combobox (a text input with suggestions, in place of <datalist>).
 *
 * The menu opens as a popover so it sits in the top layer: a Dialog or Drawer can't clip it, and a modal <dialog>
 * doesn't make it inert. Where the popover API is missing (jsdom) the menu still renders, just not in the top layer.
 */

/** What onChange receives: callers read e.target.value, as they did from the native element. */
export interface DropdownChange {
  target: { value: string };
}

interface Opt {
  value: string;
  label: ReactNode;
  text: string;
  disabled: boolean;
}

const textOf = (n: ReactNode): string =>
  n == null || typeof n === 'boolean'
    ? ''
    : typeof n === 'string' || typeof n === 'number'
      ? String(n)
      : Array.isArray(n)
        ? n.map(textOf).join('')
        : isValidElement<{ children?: ReactNode }>(n)
          ? textOf(n.props.children)
          : '';

/** The <option> children, through fragments, as a flat list. */
function readOptions(children: ReactNode): Opt[] {
  const out: Opt[] = [];
  const walk = (nodes: ReactNode) =>
    Children.forEach(nodes, (c) => {
      if (!isValidElement(c)) return;
      const el = c as ReactElement<{ children?: ReactNode; value?: unknown; disabled?: boolean }>;
      if (el.type === Fragment) return walk(el.props.children);
      if (el.type !== 'option') return;
      const text = textOf(el.props.children);
      out.push({ value: el.props.value === undefined ? text : String(el.props.value), label: el.props.children, text, disabled: !!el.props.disabled });
    });
  walk(children);
  return out;
}

/** Where a menu goes: under its anchor or, without room, over it. */
function placeFor(a: HTMLElement): CSSProperties {
  const r = a.getBoundingClientRect();
  const below = window.innerHeight - r.bottom - 8;
  const above = r.top - 8;
  const up = below < 200 && above > below;
  const room = Math.max(120, Math.min(320, up ? above : below));
  return {
    left: r.left,
    minWidth: r.width,
    maxWidth: Math.max(r.width, Math.min(480, window.innerWidth - r.left - 8)),
    maxHeight: room,
    ...(up ? { bottom: window.innerHeight - r.top + 4, top: 'auto' } : { top: r.bottom + 4, bottom: 'auto' }),
  };
}

/** Positions a popover menu under (or, without room, over) its anchor, and keeps it there while open. */
function useMenu(anchor: React.RefObject<HTMLElement | null>, open: boolean, close: () => void) {
  const menu = useRef<HTMLUListElement>(null);
  const [style, setStyle] = useState<CSSProperties>({});
  useLayoutEffect(() => {
    const m = menu.current as (HTMLUListElement & { showPopover?: () => void; hidePopover?: () => void }) | null;
    if (!open || !m || !anchor.current) return;
    setStyle(placeFor(anchor.current));
    try {
      m.showPopover?.();
    } catch {
      // already showing
    }
    return () => {
      try {
        m.hidePopover?.();
      } catch {
        // already hidden
      }
    };
  }, [open, anchor]);
  useEffect(() => {
    if (!open) return;
    const place = () => anchor.current && setStyle(placeFor(anchor.current));
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!anchor.current?.contains(t) && !menu.current?.contains(t)) close();
    };
    // Scrolling the page moves the anchor; scrolling inside the menu doesn't.
    const onScroll = (e: Event) => {
      if (!menu.current?.contains(e.target as Node)) place();
    };
    document.addEventListener('pointerdown', onDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', place);
    };
  }, [open, anchor, close]);
  return { menu, style };
}

/** Keeps the active option in view as the arrow keys move it. */
function useScrollActive(menu: React.RefObject<HTMLUListElement | null>, open: boolean, active: number) {
  useEffect(() => {
    if (!open || active < 0) return;
    (menu.current?.children[active] as HTMLElement | undefined)?.scrollIntoView?.({ block: 'nearest' });
  }, [menu, open, active]);
}

// React's types here predate the popover attribute. Without the API (jsdom) the menu renders in place.
const POPOVER = (typeof HTMLElement !== 'undefined' && 'showPopover' in HTMLElement.prototype ? { popover: 'manual' } : {}) as Record<string, string>;

const step = (opts: Opt[], from: number, by: 1 | -1) => {
  for (let i = from + by; i >= 0 && i < opts.length; i += by) if (!opts[i]!.disabled) return i;
  return from;
};
const first = (opts: Opt[]) => step(opts, -1, 1);
const last = (opts: Opt[]) => step(opts, opts.length, -1);

const Check = () => (
  <svg className={s.check} width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <path d="M2.5 7.5 5.5 10.5 11.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export interface SelectProps {
  value?: string | number;
  onChange?: (e: DropdownChange) => void;
  children?: ReactNode;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  className?: string;
  style?: CSSProperties;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

/** Pick one of the <option> children. A drop-in for a native <select>, drawn in the portal's style. */
export function Select({ value, onChange, children, disabled, required, id, className, style, ...aria }: SelectProps) {
  const opts = useMemo(() => readOptions(children), [children]);
  const current = String(value ?? '');
  // Like a native select, an unknown value shows the first option.
  const selected = Math.max(0, opts.findIndex((o) => o.value === current));
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const button = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const close = useCallback(() => setOpen(false), []);
  const { menu, style: menuStyle } = useMenu(button, open, close);
  useScrollActive(menu, open, active);
  const typed = useRef({ text: '', at: 0 });

  const show = (at = selected) => {
    if (disabled || opts.length === 0) return;
    setActive(opts[at]?.disabled ? first(opts) : at);
    setOpen(true);
  };
  const choose = (i: number) => {
    const o = opts[i];
    if (!o || o.disabled) return;
    setOpen(false);
    button.current?.focus();
    if (o.value !== current) onChange?.({ target: { value: o.value } });
  };
  const typeahead = (key: string) => {
    const now = Date.now();
    const t = typed.current;
    t.text = now - t.at > 700 ? key : t.text + key;
    t.at = now;
    const from = open ? active : selected;
    const order = [...opts.keys()].map((n) => (from + 1 + n) % opts.length);
    const hit = order.find((n) => !opts[n]!.disabled && opts[n]!.text.toLowerCase().startsWith(t.text.toLowerCase()));
    if (hit === undefined) return;
    if (open) setActive(hit);
    else choose(hit);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    const k = e.key;
    if (!open) {
      if (k === 'ArrowDown' || k === 'ArrowUp' || k === 'Enter' || k === ' ') {
        e.preventDefault();
        show();
      } else if (k.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) typeahead(k);
      return;
    }
    if (k === 'Escape') {
      // Close the menu, not the dialog around it.
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    } else if (k === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => step(opts, a, 1));
    } else if (k === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => step(opts, a, -1));
    } else if (k === 'Home' || k === 'PageUp') {
      e.preventDefault();
      setActive(first(opts));
    } else if (k === 'End' || k === 'PageDown') {
      e.preventDefault();
      setActive(last(opts));
    } else if (k === 'Enter' || k === ' ') {
      e.preventDefault();
      choose(active);
    } else if (k === 'Tab') {
      setOpen(false);
    } else if (k.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      typeahead(k);
    }
  };

  const shown = opts[selected];
  return (
    <div className={[s.select, className].filter(Boolean).join(' ')} style={style}>
      <button
        ref={button}
        type="button"
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        aria-required={required || undefined}
        data-value={current}
        {...aria}
        disabled={disabled}
        className={s.trigger}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={onKeyDown}
        onBlur={(e) => {
          if (!menu.current?.contains(e.relatedTarget as Node)) setOpen(false);
        }}
      >
        <span className={s.value}>{shown?.label ?? ' '}</span>
        <Chevron />
      </button>
      {open && (
        <ul ref={menu} id={listId} role="listbox" {...POPOVER} className={s.menu} style={menuStyle} aria-label={aria['aria-label']} aria-labelledby={aria['aria-label'] || !id ? undefined : id}>
          {opts.map((o, i) => (
            <li
              key={`${o.value}-${i}`}
              id={`${listId}-${i}`}
              role="option"
              data-value={o.value}
              aria-selected={i === selected}
              aria-disabled={o.disabled || undefined}
              data-active={i === active || undefined}
              className={s.option}
              // Keep focus on the button so the keyboard keeps working.
              onMouseDown={(e) => e.preventDefault()}
              onMouseMove={() => !o.disabled && active !== i && setActive(i)}
              onClick={() => choose(i)}
            >
              <span className={s.optionText}>{o.label}</span>
              {i === selected && <Check />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const Chevron = () => (
  <svg className={s.chevron} width="12" height="8" viewBox="0 0 12 8" aria-hidden="true">
    <path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

export interface ComboboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'list'> {
  value: string;
  onChange: (e: DropdownChange) => void;
  /** Suggestions. The person can still type something that isn't one. */
  options: ReadonlyArray<string | { value: string; label?: string }>;
}

/** A text input with branded suggestions, in place of <input list> and a <datalist>. */
export function Combobox({ value, onChange, options, className, onKeyDown, onFocus, onBlur, disabled, ...rest }: ComboboxProps) {
  const all = useMemo<Opt[]>(
    () => options.map((o) => (typeof o === 'string' ? { value: o, label: o, text: o, disabled: false } : { value: o.value, label: o.label ?? o.value, text: o.label ?? o.value, disabled: false })),
    [options],
  );
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  // Until the person types, show every suggestion, not just those matching what's already there.
  const [filtering, setFiltering] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const close = useCallback(() => setOpen(false), []);
  const { menu, style: menuStyle } = useMenu(input, open && all.length > 0, close);
  const q = value.trim().toLowerCase();
  const shown = useMemo(() => (filtering && q ? all.filter((o) => o.value.toLowerCase().includes(q) || o.text.toLowerCase().includes(q)) : all), [all, filtering, q]);
  const visible = open && shown.length > 0;
  useScrollActive(menu, visible, active);

  const show = () => {
    if (disabled || all.length === 0) return;
    setFiltering(false);
    setActive(all.findIndex((o) => o.value === value));
    setOpen(true);
  };
  const choose = (i: number) => {
    const o = shown[i];
    if (!o) return;
    setOpen(false);
    if (o.value !== value) onChange({ target: { value: o.value } });
  };

  return (
    <div className={s.combo}>
      <input
        ref={input}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={visible}
        aria-controls={all.length ? listId : undefined}
        aria-activedescendant={visible && active >= 0 ? `${listId}-${active}` : undefined}
        autoComplete="off"
        {...rest}
        disabled={disabled}
        value={value}
        className={[s.comboInput, all.length ? s.hasChevron : '', className].filter(Boolean).join(' ')}
        onChange={(e) => {
          onChange({ target: { value: e.target.value } });
          setFiltering(true);
          setActive(-1);
          if (all.length) setOpen(true);
        }}
        onClick={() => !open && show()}
        onFocus={onFocus}
        onBlur={(e) => {
          if (!menu.current?.contains(e.relatedTarget as Node)) setOpen(false);
          onBlur?.(e);
        }}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          if (e.defaultPrevented) return;
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!visible) return show();
            const by = e.key === 'ArrowDown' ? 1 : -1;
            setActive((a) => (a < 0 ? (by === 1 ? 0 : shown.length - 1) : Math.min(shown.length - 1, Math.max(0, a + by))));
          } else if (e.key === 'Enter' && visible && active >= 0) {
            e.preventDefault();
            choose(active);
          } else if (e.key === 'Escape' && visible) {
            e.preventDefault();
            e.stopPropagation();
            setOpen(false);
          } else if (e.key === 'Tab') setOpen(false);
        }}
      />
      {all.length > 0 && (
        <span className={s.comboChevron} aria-hidden="true" onMouseDown={(e) => e.preventDefault()} onClick={() => (open ? setOpen(false) : (input.current?.focus(), show()))}>
          <Chevron />
        </span>
      )}
      {visible && (
        <ul ref={menu} id={listId} role="listbox" {...POPOVER} className={s.menu} style={menuStyle} aria-label="Suggestions">
          {shown.map((o, i) => (
            <li
              key={`${o.value}-${i}`}
              id={`${listId}-${i}`}
              role="option"
              data-value={o.value}
              aria-selected={o.value === value}
              data-active={i === active || undefined}
              className={s.option}
              onMouseDown={(e) => e.preventDefault()}
              onMouseMove={() => active !== i && setActive(i)}
              onClick={() => choose(i)}
            >
              <span className={s.optionText}>
                {o.label}
                {o.text !== o.value && <span className={s.optionMeta}>{o.value}</span>}
              </span>
              {o.value === value && <Check />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
